import { useState } from 'react'
import { Button, Form, InputNumber, Message, Space } from '@arco-design/web-react'
import { clearChannelAffinityCacheApi, getChannelAffinityCacheStatsApi } from '@/api/option'
import JsonField, { validateJson } from '../JsonField'
import InstantSwitch from '../InstantSwitch'
import SettingSectionCard from '../SettingSectionCard'
import type { OptionsAPI } from '../useOptions'

const KEYS = [
  'channel_affinity_setting.enabled',
  'channel_affinity_setting.switch_on_success',
  'channel_affinity_setting.max_entries',
  'channel_affinity_setting.default_ttl_seconds',
  'channel_affinity_setting.rules',
]

export default function ChannelAffinitySection({ options }: { options: OptionsAPI }) {
  const { get, getNumber, patch } = options
  const [stats, setStats] = useState<string>('')
  const [clearing, setClearing] = useState(false)

  const validate = () => {
    const msg = validateJson(get('channel_affinity_setting.rules'))
    if (msg) return `channel_affinity_setting.rules: ${msg}`
    return null
  }

  const loadStats = async () => {
    try {
      const data = await getChannelAffinityCacheStatsApi()
      setStats(JSON.stringify(data, null, 2))
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '获取缓存统计失败')
    }
  }

  const clearCache = async (all = false) => {
    setClearing(true)
    try {
      await clearChannelAffinityCacheApi(all)
      Message.success(all ? '已清空全部渠道亲和缓存' : '已清空默认规则缓存')
      await loadStats()
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '清空失败')
    } finally {
      setClearing(false)
    }
  }

  return (
    <SettingSectionCard title="渠道亲和" keys={KEYS} options={options} validate={validate}>
      <Form layout="vertical">
        <div className="grid md:grid-cols-2 gap-3">
          <Form.Item label="启用渠道亲和">
            <InstantSwitch options={options} optionKey="channel_affinity_setting.enabled" defaultChecked />
          </Form.Item>
          <Form.Item label="成功后切换渠道">
            <InstantSwitch options={options} optionKey="channel_affinity_setting.switch_on_success" defaultChecked />
          </Form.Item>
          <Form.Item label="最大缓存条目">
            <InputNumber
              style={{ width: 240 }}
              min={0}
              value={getNumber('channel_affinity_setting.max_entries', 100000)}
              onChange={(v) => patch({ 'channel_affinity_setting.max_entries': v ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="默认 TTL（秒）">
            <InputNumber
              style={{ width: 240 }}
              min={0}
              value={getNumber('channel_affinity_setting.default_ttl_seconds', 3600)}
              onChange={(v) => patch({ 'channel_affinity_setting.default_ttl_seconds': v ?? 0 })}
            />
          </Form.Item>
        </div>
        <Form.Item label="亲和规则 JSON" extra="与 new-api channel_affinity_setting.rules 结构一致">
          <JsonField
            value={get('channel_affinity_setting.rules')}
            onChange={(v) => patch({ 'channel_affinity_setting.rules': v })}
            rows={10}
          />
        </Form.Item>
        <Space wrap>
          <Button onClick={loadStats}>查看缓存统计</Button>
          <Button loading={clearing} onClick={() => clearCache(false)}>
            清空缓存
          </Button>
          <Button status="warning" loading={clearing} onClick={() => clearCache(true)}>
            清空全部
          </Button>
        </Space>
        {stats && (
          <pre className="mt-3 p-3 bg-slate-50 rounded-lg text-xs overflow-auto max-h-48">{stats}</pre>
        )}
      </Form>
    </SettingSectionCard>
  )
}
